import { isField, isItem } from './mappers';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** One component's clashing group members: folder key (see `folderKey`) → field name → Sanity name. */
export type CollisionNames = Readonly<Record<string, Readonly<Record<string, string>>>>;

/**
 * A Sitecore section name and the payload folder it arrives as, reduced to the same key. Sitecore
 * spells a section the way it was authored (`Generic error`, `Opt-in`) and the flow payloads
 * camel-case it (`genericError`, `optIn`), so only letters and digits are compared, lower-cased.
 */
export const folderKey = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * A record's own entry, never one inherited from `Object.prototype`: the keys looked up here are
 * folder and field names from the CMS, and nothing stops one being called `constructor`.
 */
const own = <T>(record: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.hasOwn(record, key) ? record[key] : undefined;

/**
 * A multilist, which is a field like any other but arrives as the list of items it references, with
 * no `value` wrapper. An empty one is `[]`. Without this, a group holding one, such as the SME usage
 * step's `estimationHelp` with its industry choice, was not recognised, and none of its fields lifted.
 */
const isItemList = (value: unknown): boolean => Array.isArray(value) && value.every(isItem);

/**
 * The authored field names inside a field group, or `undefined` when this is not a group.
 *
 * Sitecore serialises a group in two shapes, and both occur in this repo:
 *
 * - as a referenced item, `{ id, name, fields: { title: { value } } }`
 * - as a plain object of fields, `{ title: { value } }`, which is what the flow payloads use
 *   (`mocks/sitecore/apps/flows/legislationCompassFlow/legislationStep.ts`)
 *
 * The second is why this cannot simply ask `isItem`. The risk in accepting it is mistaking an
 * object-valued *field* for a group: a General Link arrives as
 * `{ value: { text, href, linktype } }`, and lifting its members would put `href` on the block.
 * `isField` separates them, since a field always carries `value` and nothing but metadata beside
 * it, and a group never carries `value` at all.
 *
 * Requiring every member to be a field, an item or a list of items is what keeps this narrow. An
 * object with one plain string among its members is content of some other shape, and is left alone.
 */
const groupMembers = (raw: unknown): string[] | undefined => {
  if (isItem(raw)) return Object.keys(raw.fields);
  if (!isPlainObject(raw) || isField(raw)) return undefined;

  const members = Object.keys(raw);
  if (members.length === 0) return undefined;
  return members.every(member => isField(raw[member]) || isItem(raw[member]) || isItemList(raw[member]))
    ? members
    : undefined;
};

/**
 * TRANSITIONAL. While this is `true` a lifted field is readable twice: under its own name, and
 * where it has always been, inside the Sitecore field group it arrived in.
 *
 * That is what lets each component move to the flat names on its own schedule rather than every
 * reader changing in one commit.
 *
 * Flipping it to `false` is the last step of the migration, not its safety net. It stops the groups
 * being emitted; it does not by itself make a stale reader fail. A component's content type is a
 * hand-written claim rather than anything derived from this adapter, so a declared
 * `content?: { title?: string }` still compiles and simply reads `undefined`, which is the silent
 * failure this whole change exists to remove. What makes a stale reader fail is deleting that
 * declaration from its own content type, which each component's story does as it migrates. Flip
 * this once the last one has.
 */
const KEEP_SITECORE_FIELD_GROUPS = true;

/**
 * Lifts a rendering's Sitecore field groups onto the rendering itself.
 *
 * Sitecore builds a component's field groups out of the datasource's child items, so a step's
 * title arrives as `content.title`. Sanity stores the same field flat on the block, as `title`.
 * Components should not have to know either of those things, so both adapters put the field where
 * the other one does: here by lifting the group, and on the Sanity side by naming each family
 * entry after the role it already carries.
 *
 * Needs no table for most fields. A field group is exactly a datasource child item, which the
 * payload identifies, and a field's name inside the group is the name Sanity gave it. The exception
 * is a name that two sections of the same component share, which is what `collisionNames` is for.
 *
 * Note the difference from what the adapter already did. `unwrapFields` removes JSS's transport
 * wrappers, turning `{ value: 'x' }` into `'x'`, and dissolves an item's `fields` sub-object. The
 * group key itself survived that. This is what removes the group.
 *
 * Deleted with this package when Sitecore is switched off. Nothing here describes Sanity.
 */
export const liftFieldGroups = (
  content: Record<string, unknown>,
  /** The rendering's raw JSS fields, which is what says whether a key was a datasource child. */
  rawFields: Record<string, unknown> | undefined,
  /** The Sanity names of this component's clashing group members, from `collision-names.gen.ts`. */
  collisionNames?: CollisionNames,
  /** Overridable so a test can assert the same fields lift either way. See the constant. */
  keepGroups: boolean = KEEP_SITECORE_FIELD_GROUPS,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  const groups: Array<{ key: string; unwrapped: Record<string, unknown>; authored: string[] }> = [];
  /**
   * Names already spoken for on this rendering: its own fields, and the groups themselves.
   *
   * Collected whether or not the groups are being kept, so that flipping
   * `KEEP_SITECORE_FIELD_GROUPS` changes what is emitted but never which fields are lifted. Reading
   * `out` instead would make a member that matches its own group's name unliftable today and
   * liftable after the flip, which is the sort of difference that only shows up long after anyone
   * is looking for it.
   */
  const taken = new Set(Object.keys(content));

  for (const [key, value] of Object.entries(content)) {
    // Only authored field names are liftable. On the item shape `unwrapFields` merges the item's
    // metadata (`id`, `name`, `url`, `displayName`) into the same object, and lifting that would
    // put an item's bookkeeping on the block as though an author had written it.
    const authored = groupMembers(rawFields?.[key]);
    if (authored && isPlainObject(value)) {
      groups.push({ key, unwrapped: value, authored });
      if (keepGroups) out[key] = value;
      continue;
    }
    out[key] = value;
  }

  // A name carried by two different groups is not lifted under that name: picking one would depend
  // on key order, which is not something to decide a rendered value by. Sanity gives each copy a
  // name of its own instead, and those are emitted further down.
  const liftCount = new Map<string, number>();
  for (const { unwrapped, authored } of groups) {
    for (const member of authored) {
      if (member in unwrapped) liftCount.set(member, (liftCount.get(member) ?? 0) + 1);
    }
  }

  for (const { unwrapped, authored } of groups) {
    for (const member of authored) {
      if (!(member in unwrapped)) continue;
      if (liftCount.get(member) !== 1) continue;
      // A name the rendering already uses wins over one from a group, whether that is a field of
      // its own or one of the groups.
      if (taken.has(member)) continue;
      out[member] = unwrapped[member];
    }
  }

  // Each copy of a clashing name, under the name Sanity gave it: its section's name in front
  // (`Content_title`, `moreInfoDialog_title`). Neither which fields have one nor how the section is
  // spelled can be read off the payload, so both come from the CMS team's mapping file.
  if (collisionNames) {
    for (const { key, unwrapped, authored } of groups) {
      const names = own(collisionNames, folderKey(key));
      if (!names) continue;
      for (const member of authored) {
        const sanityName = own(names, member);
        if (sanityName === undefined || !(member in unwrapped)) continue;
        // Additive like everything else here: a name the rendering already has, of its own or
        // lifted, is never overwritten.
        if (taken.has(sanityName) || sanityName in out) continue;
        out[sanityName] = unwrapped[member];
      }
    }
  }

  return out;
};

/**
 * Lifts the field groups of every referenced item, the way `liftFieldGroups` lifts a rendering's own.
 *
 * Sitecore hands a multilist or treelist as the items it references, and a droplink as its one item,
 * and an item's fields can sit in field groups of their own: a dongle offer product arrives as
 * `{ id, name, fields: { product: { title: { value } } } }`, so after `unwrapFields` its title is
 * `item.product.title`. Sanity stores the referenced document's fields on the item itself, as
 * `item.title`. Without this, a step reading a list item by its field names finds them on one lane only.
 *
 * Additive, like the lift it reuses: the groups stay, and a name the item already has is never
 * overwritten, its `id`, `url` and `displayName` included. The one exception is `name`: see `liftItem`.
 * An item's groups are told by its raw fields, so this walks the raw fields beside the unwrapped content,
 * into groups and into the items of an item's own lists, however deep. Items have no component name, so
 * no collision names apply: a name carried by two of an item's groups is left in its groups.
 */
export const liftReferencedItemGroups = (
  content: Record<string, unknown>,
  /** The rendering's raw JSS fields, which say what was an item and what was a group. */
  rawFields: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  if (!rawFields) return content;
  const out: Record<string, unknown> = { ...content };
  for (const [key, raw] of Object.entries(rawFields)) {
    if (key in out) out[key] = liftItemsIn(raw, out[key]);
  }
  return out;
};

/** `unwrapped` with every item in it lifted, `raw` being what it was unwrapped from. */
const liftItemsIn = (raw: unknown, unwrapped: unknown): unknown => {
  if (Array.isArray(raw)) {
    return Array.isArray(unwrapped) && unwrapped.length === raw.length
      ? unwrapped.map((entry, index) => liftItemsIn(raw[index], entry))
      : unwrapped;
  }
  if (!isPlainObject(unwrapped)) return unwrapped;
  if (isItem(raw)) return liftItem(raw, unwrapped);
  // A group: its members can hold lists of their own. A field's value is content, never walked.
  if (isPlainObject(raw) && !isField(raw)) return liftReferencedItemGroups(unwrapped, raw);
  return unwrapped;
};

/**
 * One referenced item with its groups lifted, its fields under their own names, as Sanity stores an item.
 *
 * That includes `name`: a field of that name in one of the item's groups takes `name`, as Sanity's `name`
 * is that field. A B2B lead product option's `name` is then the product it stands for
 * (`enc_elektriciteitgas`), and its own Sitecore name (`electricityAndGas`) moves to `itemName`, where
 * Sanity keeps it. `unwrapFields` already lets a field the item holds directly take `name`; this does the
 * same for one in a group. An item without such a field keeps its own name as `name`, and nothing else.
 */
const liftItem = (
  raw: Record<string, unknown> & { fields: Record<string, unknown> },
  unwrapped: Record<string, unknown>,
): Record<string, unknown> => {
  if (Object.hasOwn(raw.fields, 'name')) {
    return liftFieldGroups(liftReferencedItemGroups(unwrapped, raw.fields), raw.fields);
  }
  const { name: itemName, ...withoutName } = unwrapped;
  const lifted = liftFieldGroups(liftReferencedItemGroups(withoutName, raw.fields), raw.fields);
  if (!('name' in lifted)) return itemName === undefined ? lifted : { ...lifted, name: itemName };
  return 'itemName' in lifted || itemName === undefined ? lifted : { ...lifted, itemName };
};
